import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import {
  AVAILABLE_LOCATIONS_DROPPABLE_ID,
  SELECTED_LOCATIONS_DROPPABLE_ID,
  isLocationVisible,
} from './utils';
import DraggableLocation from './DraggableLocation';
import SearchInput from '../../../../../common/SearchInput';
import { t } from '../../../../../../modules/infra/i18n';

const Locations = ({
  canModify,
  submitting,
  availableLocations,
  selectedLocations,
  onLocationDrop,
  onSelectLocation,
  onDeselectLocation,
  onSelectAll,
  onDeselectAll,
  locationsFilter,
  setLocationsFilter,
}) => (
  <div className="c-wrapper" data-automation-id="locations-tab">
    <h2 className="c-heading u-mb-">{t('barber:locations.assign_heading')}</h2>
    <p className="u-mb">
      {t('barber:locations.assign_description')}
    </p>

    <div className="layout">
      <div className="layout__item">
        <DragDropContext onDragEnd={onLocationDrop}>
          <div className="c-dragndrop-areas-layout u-mt--">
            <div className="c-dragndrop-area-column u-height-735 u-scrollable">
              <div className="c-label-new">
                {t('barber:locations.available_locations')}
                &nbsp;
                <span className="c-counter-badge">{availableLocations.length}</span>
              </div>
              <Droppable droppableId={AVAILABLE_LOCATIONS_DROPPABLE_ID}>
                {provided => (
                  <div
                    className="c-dragndrop-area-column__items u-pt-"
                    data-automation-id="available-locations"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <SearchInput
                      placeholder={t('barber:locations.search_placeholder')}
                      setSearchTerm={setLocationsFilter}
                    />
                    {availableLocations.map((location, index) => (
                      <DraggableLocation
                        key={location.id}
                        display-if={isLocationVisible(location, locationsFilter)}
                        index={index}
                        canModifyLocations={canModify && !submitting}
                        location={location}
                        onLocationClick={onSelectLocation(location.id)}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="c-dragndrop-area__buttons u-pt">
              <button
                className="c-btn c-btn--small c-btn--white u-mb--"
                type="button"
                onClick={onSelectAll}
                disabled={!canModify || submitting}
                data-automation-id="select-all-locations-button"
              >
                {'>>'}
              </button>
              <button
                className="c-btn c-btn--small c-btn--white u-mt--"
                type="button"
                onClick={onDeselectAll}
                disabled={!canModify || submitting}
                data-automation-id="deselect-all-locations-button"
              >
                {'<<'}
              </button>
            </div>
            <div className="c-dragndrop-area-column u-height-735 u-scrollable">
              <div className="c-label-new">
                {t('barber:locations.selected_locations')}
                &nbsp;
                <span className="c-counter-badge">{selectedLocations.length}</span>
              </div>
              <Droppable droppableId={SELECTED_LOCATIONS_DROPPABLE_ID}>
                {provided => (
                  <div
                    className="c-dragndrop-area-column__items"
                    data-automation-id="selected-locations"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {selectedLocations.map((location, index) => (
                      <DraggableLocation
                        key={location.id}
                        index={index}
                        canModifyLocations={canModify && !submitting}
                        location={location}
                        onLocationClick={onDeselectLocation(location.id)}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>
      </div>
    </div>
  </div>
);

Locations.propTypes = {
  canModify: PropTypes.bool.isRequired,
  availableLocations: PropTypes.array,
  selectedLocations: PropTypes.array,
  onLocationDrop: PropTypes.func.isRequired,
  onSelectLocation: PropTypes.func.isRequired,
  onDeselectLocation: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  locationsFilter: PropTypes.string,
  setLocationsFilter: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

Locations.defaultProps = {
  canModify: true,
  availableLocations: [],
  filteredAvailableLocations: [],
  selectedLocations: [],
  locationsFilter: '',
  submitting: false,
};

export default Locations;
